import { useEffect, useMemo, useState } from "react";
import API from "../../api/api";
import {
    useAuth
} from "../../context/AuthContext";
import useIsMobile from "../../hooks/useIsMobile";
import { useNavigate } from "react-router-dom";
import "./Tours-title-ellipsis.css";


export default function AvailableTours() {
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const [tours, setTours] = useState([]);
  const [loading, setLoading] = useState(true);
  

  /* ================= AUTH Permissions ================= */
  const {
    hasPermission
} = useAuth();

const canSelfAssign =
    hasPermission(
        "guide_self_assign"
    );

    const uniqueTours = useMemo(() => {

    return [...new Set(
        tours.map(t => t.title)
    )].sort();

}, [tours]);

  /* ================= FILTER STATE ================= */
  const FILTER_STORAGE_KEY = "guide_available_tours_filters";

const defaultFilters = {
    date: "30",
    specificDate: "",
    tour: [],
    language: "all",
    bookings: "all"
};

const [filters, setFilters] = useState(() => {
    try {
        const saved = localStorage.getItem(FILTER_STORAGE_KEY);

        if (!saved) {
            return defaultFilters;
        }

       const parsed = JSON.parse(saved);

return {
    ...defaultFilters,
    ...parsed,
    tour: Array.isArray(parsed.tour)
        ? parsed.tour
        : parsed.tour === "all" || !parsed.tour
            ? []
            : [parsed.tour]
};

    } catch (err) {
        console.error(
            "Unable to load saved guide filters:",
            err
        );

        return defaultFilters;
    }
});

useEffect(() => {
    localStorage.setItem(
        FILTER_STORAGE_KEY,
        JSON.stringify(filters)
    );
}, [filters]);

const [showFilters, setShowFilters] = useState(false);
const [tourDropdownOpen, setTourDropdownOpen] = useState(false);

const toggleTour = (tourName) => {
    setFilters(prev => {
        const current = Array.isArray(prev.tour)
            ? prev.tour
            : [];

        const exists = current.includes(tourName);

        return {
            ...prev,
            tour: exists
                ? current.filter(name => name !== tourName)
                : [...current, tourName]
        };
    });
};

const clearTourFilter = () => {
    setFilters(prev => ({
        ...prev,
        tour: []
    }));
};

const [assignModal, setAssignModal] = useState({
    open: false,
    tourId: null,
    status: "confirm"
});



  /* ================= DATE FORMATTER ================= */
  const formatDateTime = (dateString) => {
    if (!dateString) return "No date";

    const date = new Date(dateString);

    const weekday = date.toLocaleDateString("en-GB", {
      weekday: "long"
    });

    const day = String(date.getDate()).padStart(2, "0");

    const month = date.toLocaleDateString("en-GB", {
      month: "long"
    });

    const year = date.getFullYear();

    const time = date.toLocaleTimeString("en-GB", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: false
    });

    return `${weekday}, ${day} ${month} ${year} • ${time}`;
  };

  /* ================= LOAD ================= */
  const load = async () => {
    try {
      setLoading(true);
      const res = await API.get("/guide/available");
      setTours(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error("load available tours error:", err);
      setTours([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  /* ================= ASSIGN ================= */
  const assign = async (id) => {
  try {
    await API.post(`/guide/assign/${id}`);

    // Remove from Available Tours
    setTours(prev => prev.filter(t => t.id !== id));

    // Show success popup
    setAssignModal({
      open: true,
      tourId: id,
      status: "success"
    });

    // Refresh list
    load();

  } catch (err) {
    console.error("assign error:", err);

    setAssignModal({
      open: true,
      tourId: id,
      status: "not-assigned"
    });
  }
};

const openAssignModal = (id) => {

    setAssignModal({
        open: true,
        tourId: id,
        status: "confirm"
    });

};

const closeAssignModal = () => {

    setAssignModal({
        open: false,
        tourId: null,
        status: "confirm"
    });

};

  /* ================= FILTERED DATA ================= */

const languages = useMemo(() => {

    return [...new Set(
        tours
            .map(t => t.languages)
            .filter(Boolean)
    )].sort();

}, [tours]);

const filteredTours = useMemo(() => {

    return tours.filter((t) => {

        const matchBookings = (() => {

            if (filters.bookings === "all") {
                return true;
            }

            if (filters.bookings === "booked") {
                return (t.booked ?? 0) > 0;
            }

            if (filters.bookings === "empty") {
                return (t.booked ?? 0) === 0;
            }

            return true;

        })();

       

        const selectedTours = Array.isArray(filters.tour)
    ? filters.tour
    : [];

const matchTour =
    selectedTours.length === 0
    || selectedTours.includes(t.title);

        const matchLanguage =
            filters.language === "all"
            || (t.languages || "")
                .split(",")
                .map(l => l.trim())
                .includes(filters.language);

        const matchDate = (() => {

            if (!t.start_datetime) return false;

            const tourDate = new Date(t.start_datetime);
            const now = new Date();

            /* Specific date overrides all other date filters */
            if (filters.specificDate) {

                return (
                    tourDate.toISOString().slice(0, 10) ===
                    filters.specificDate
                );

            }

            if (filters.date === "all") {
                return true;
            }

            if (filters.date === "today") {
                return (
                    tourDate.toDateString() ===
                    now.toDateString()
                );
            }

            if (filters.date === "7") {

                const end = new Date(now);
                end.setDate(end.getDate() + 7);

                return tourDate >= now && tourDate <= end;
            }

            if (filters.date === "30") {

                const end = new Date(now);
                end.setDate(end.getDate() + 30);

                return tourDate >= now && tourDate <= end;
            }

            if (filters.date === "90") {

                const end = new Date(now);
                end.setDate(end.getDate() + 90);

                return tourDate >= now && tourDate <= end;
            }

            return true;

        })();

        return (
            matchDate &&
            matchTour &&
            matchLanguage &&
            matchBookings
        );

    });

}, [tours, filters]);

  return (
    <div style={page}>

      {/* HEADER */}
      {
isMobile ?

<div style={mobileHeader}>

    <h2 style={mobileTitle}>
        Available Tours
    </h2>

</div>

:

<div style={header}>

    <h2 style={title}>
        Available Tours
    </h2>

    <p style={subtitle}>
        Browse and assign tours instantly
    </p>

</div>

}

      

     {/* ================= FILTERS ================= */}

{
isMobile ?

<>

<div style={searchBar}>



    <button

        onClick={()=>

            setShowFilters(
                !showFilters
            )

        }

        style={filterButton}

    >

        {showFilters ? "✖" : "⚙️"}

    </button>

</div>

{

showFilters &&

<div style={filterPanel}>

    <select
        value={filters.bookings}
        onChange={(e)=>

            setFilters(p=>({

                ...p,

                bookings:e.target.value

            }))

        }
        style={select}
    >

        <option value="all">
            Booking status
        </option>

        <option value="booked">
            Booked Tours
        </option>

        <option value="empty">
            Empty Tours
        </option>

    </select>

    <input

        type="date"

        value={filters.specificDate}

        onChange={(e)=>

            setFilters(p=>({

                ...p,

                specificDate:e.target.value

            }))

        }

        style={input}

    />

    <select

        value={filters.date}

        onChange={(e)=>

            setFilters(p=>({

                ...p,

                date:e.target.value

            }))

        }

        style={select}

    >

        <option value="today">
            Today
        </option>

        <option value="7">
            Next 7 Days
        </option>

        <option value="30">
            Next 30 Days
        </option>

        <option value="90">
            Next 3 Months
        </option>

    </select>

    <select

        value={filters.language}

        onChange={(e)=>

            setFilters(p=>({

                ...p,

                language:e.target.value

            }))

        }

        style={select}

    >

        <option value="all">
            All Languages
        </option>

        {

        languages.map(lang=>(

            <option
                key={lang}
                value={lang}
            >

                {lang}

            </option>

        ))

        }

    </select>

    <div style={tourFilterWrapper}>

    <button
        type="button"
        onClick={() =>
            setTourDropdownOpen(prev => !prev)
        }
        style={tourFilterButton}
    >
        <span>
            {filters.tour.length === 0
                ? "All Tours"
                : `${filters.tour.length} tour${filters.tour.length > 1 ? "s" : ""} selected`}
        </span>

        <span>
            ▾
        </span>
    </button>

    {tourDropdownOpen && (
        <div style={tourDropdown}>

            <button
                type="button"
                onClick={clearTourFilter}
                style={tourSelectAll}
            >
                {filters.tour.length === 0 ? "✓ " : ""}
                All Tours
            </button>

            {uniqueTours.map(name => {

                const checked =
                    filters.tour.includes(name);

                return (
                    <label
                        key={name}
                        style={tourOption}
                    >

                        <input
                            type="checkbox"
                            checked={checked}
                            onChange={() =>
                                toggleTour(name)
                            }
                        />

                        <span>
                            {name}
                        </span>

                    </label>
                );

            })}

        </div>
    )}

</div>

    <button
        onClick={load}
        style={refreshBtn}
    >
        Refresh
    </button>

</div>

}

</>

:

<div style={filterBar}>



    <select
        value={filters.bookings}
        onChange={(e)=>

            setFilters(p=>({

                ...p,

                bookings:e.target.value

            }))

        }
        style={{
    ...select,
    width: "100%",
    minWidth: 0,
    maxWidth: "100%",
    overflow: "hidden",
    textOverflow: "ellipsis"
}}
    >

        <option value="all">
            Booking status
        </option>

        <option value="booked">
            Booked Tours
        </option>

        <option value="empty">
            Empty Tours
        </option>

    </select>

    <input
        type="date"
        value={filters.specificDate}
        onChange={(e)=>

            setFilters(p=>({

                ...p,

                specificDate:e.target.value

            }))

        }
        style={input}
    />

    <select
        value={filters.date}
        onChange={(e)=>

            setFilters(p=>({

                ...p,

                date:e.target.value

            }))

        }
        style={select}
    >

        <option value="today">
            Today
        </option>

        <option value="7">
            Next 7 Days
        </option>

        <option value="30">
            Next 30 Days
        </option>

        <option value="90">
            Next 3 Months
        </option>

    </select>

    <select
        value={filters.language}
        onChange={(e)=>

            setFilters(p=>({

                ...p,

                language:e.target.value

            }))

        }
        style={select}
    >

        <option value="all">
            All Languages
        </option>

        {

        languages.map(lang=>(

            <option
                key={lang}
                value={lang}
            >

                {lang}

            </option>

        ))

        }

    </select>

    <div style={tourFilterWrapper}>

    <button
        type="button"
        onClick={() =>
            setTourDropdownOpen(prev => !prev)
        }
        style={tourFilterButton}
    >
        <span>
            {filters.tour.length === 0
                ? "All Tours"
                : `${filters.tour.length} tour${filters.tour.length > 1 ? "s" : ""} selected`}
        </span>

        <span>
            ▾
        </span>
    </button>

    {tourDropdownOpen && (
        <div style={tourDropdown}>

            <button
                type="button"
                onClick={clearTourFilter}
                style={tourSelectAll}
            >
                {filters.tour.length === 0 ? "✓ " : ""}
                All Tours
            </button>

            {uniqueTours.map(name => {

                const checked =
                    filters.tour.includes(name);

                return (
                    <label
                        key={name}
                        style={tourOption}
                    >

                        <input
                            type="checkbox"
                            checked={checked}
                            onChange={() =>
                                toggleTour(name)
                            }
                        />

                        <span>
                            {name}
                        </span>

                    </label>
                );

            })}

        </div>
    )}

</div>

    <button
        onClick={load}
        style={refreshBtn}
    >
        Refresh
    </button>

</div>

}

{/* ================= LOADING ================= */}
    
      {loading && (
        <div style={loadingBox}>
          Loading available tours...
        </div>
      )}

      {/* EMPTY */}
      {!loading && filteredTours.length === 0 && (
        <div style={emptyBox}>
          No tours match your filters
        </div>
      )}

      {/* GRID */}
      <div style={grid(isMobile)}>
        {filteredTours.map((t) => {

          const fillPercent =
            t.capacity && t.booked
              ? Math.round((t.booked / t.capacity) * 100)
              : 0;
          return (
            <div
              key={t.id}
              style={card(isMobile)}
              onClick={() => navigate(`/guide/tour/${t.id}`)}
            >
              <div style={cardHeader}>
                <h3 title={t.title} style={tourTitle(isMobile)}>{t.title}</h3>
                <span style={{
                  ...badge,
                  ...(fillPercent >= 100 ? fullBadge : activeBadge)
                }}>
                  {fillPercent >= 100 ? "Full" : "Open"}
                </span>
              </div>

              <div style={detailItem}>
                <span style={detailIcon}>▦</span>
                <div style={{ minWidth: 0 }}>
                  <div style={detailLabel}>DATE & TIME</div>
                  <div style={detailValue}>{formatDateTime(t.start_datetime)}</div>
                </div>
              </div>

              <div style={rateDisplay}>
                <span style={rateLabel}>Your rate</span>
                <span style={rateAmount}>
                  €{Number(t.guide_rate ?? 0).toLocaleString("en-IE", {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2
                  })}
                </span>
              </div>

              <div style={guestSection}>
                <div style={guestHeader}>
                  <div>
                    <div style={detailLabel}>GUESTS</div>
                    <strong style={guestCount}>{t.booked ?? 0}/{t.capacity ?? "-"}</strong>
                  </div>
                  <span style={percentText}>{fillPercent}% filled</span>
                </div>
                <div style={barBg}>
                  <div style={{
                    ...barFill,
                    width: `${Math.min(fillPercent, 100)}%`,
                    background: fillPercent >= 100 ? "#ef4444" : fillPercent >= 80 ? "#f59e0b" : "#22c55e"
                  }} />
                </div>
              </div>

              <div style={languageRow}>
                <span style={languageIcon}>文</span>
                <div style={{ minWidth: 0 }}>
                  <div style={detailLabel}>LANGUAGE</div>
                  <div style={detailValue}>{t.languages || "English"}</div>
                </div>
              </div>

              {canSelfAssign && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    openAssignModal(t.id);
                  }}
                  style={primaryBtn(isMobile)}
                >
                  Assign to me
                </button>
              )}
            </div>
          );
        })}
      </div>

{assignModal.open && (
    <div style={modalOverlay}>

        <div style={modal}>

            {assignModal.status === "confirm" && (
                <>
                    <h3 style={modalTitle}>
                        Assign Tour?
                    </h3>

                    <p style={modalText}>
                        Are you sure you want to assign this tour to yourself?
                    </p>

                    <div style={modalActions}>

                        <button
                            style={secondaryModalBtn}
                            onClick={() =>
                                setAssignModal({
                                    open: true,
                                    tourId: assignModal.tourId,
                                    status: "not-assigned"
                                })
                            }
                        >
                            No
                        </button>

                        <button
                            style={primaryModalBtn}
                            onClick={() =>
                                assign(assignModal.tourId)
                            }
                        >
                            Yes, Assign
                        </button>

                    </div>
                </>
            )}

            {assignModal.status === "success" && (
                <>
                    <h3 style={modalTitle}>
                        Tour Assigned
                    </h3>

                    <p style={modalText}>
                        This tour has been successfully assigned to you.
                    </p>

                    <button
                        style={primaryModalBtn}
                        onClick={closeAssignModal}
                    >
                        Close
                    </button>
                </>
            )}

            {assignModal.status === "not-assigned" && (
                <>
                    <h3 style={modalTitle}>
                        Tour Not Assigned
                    </h3>

                    <p style={modalText}>
                        The tour was not assigned to you.
                    </p>

                    <button
                        style={secondaryModalBtn}
                        onClick={closeAssignModal}
                    >
                        Close
                    </button>
                </>
            )}

        </div>

    </div>
)}

    </div>
  );
}

/* ================= STYLES ================= */

const rateDisplay = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: 12,
  padding: "11px 12px",
  margin: "14px 0 0",
  background: "#fff7f2",
  border: "1px solid #f0d5c9",
  borderRadius: 8
};

const rateLabel = {
  color: "#7c3b27",
  fontSize: 13,
  fontWeight: 650
};

const rateAmount = {
  color: "#7c3b27",
  fontSize: 17,
  fontWeight: 800,
  fontVariantNumeric: "tabular-nums"
};

const page = {
  padding: "24px",
  background: "#f5f6f8",
  minHeight: "100vh",
  boxSizing: "border-box",
  color: "#172033"
};

const header = {
  marginBottom: 20
};

const title = {
  margin: 0,
  fontSize: 28,
  lineHeight: 1.2,
  fontWeight: 750,
  color: "#111827",
  letterSpacing: "-0.5px"
};

const subtitle = {
  marginTop: 6,
  marginBottom: 0,
  fontSize: 14,
  color: "#718096"
};

const filterBar = {
  display: "grid",
  gridTemplateColumns: "repeat(5, minmax(125px, 1fr)) auto",
  alignItems: "end",
  gap: 12,
  padding: 18,
  marginBottom: 22,
  background: "#ffffff",
  border: "1px solid #dce2e9",
  borderRadius: 11,
  boxShadow: "0 1px 2px rgba(15, 23, 42, 0.03)"
};

const input = {
  width: "100%",
  minWidth: 0,
  boxSizing: "border-box",
  padding: "10px 12px",
  borderRadius: 8,
  border: "1px solid #d5dce5",
  background: "#ffffff",
  color: "#263247",
  fontSize: 13,
  outlineColor: "#c75028"
};

const select = {
  ...input,
  maxWidth: "100%"
};

const refreshBtn = {
  minHeight: 40,
  padding: "10px 16px",
  borderRadius: 8,
  border: "none",
  background: "#c75028",
  color: "#ffffff",
  fontSize: 13,
  fontWeight: 650,
  cursor: "pointer",
  whiteSpace: "nowrap"
};

const loadingBox = {
  padding: 22,
  background: "#ffffff",
  borderRadius: 11,
  border: "1px solid #dce2e9",
  color: "#718096",
  marginBottom: 16
};

const emptyBox = {
  padding: 34,
  textAlign: "center",
  background: "#ffffff",
  borderRadius: 11,
  border: "1px solid #dce2e9",
  color: "#718096"
};

const grid = (isMobile) => ({
  display: "grid",
  gridTemplateColumns: isMobile
    ? "minmax(0, 1fr)"
    : "repeat(2, minmax(0, 1fr))",
  gap: isMobile ? 12 : 16,
  alignItems: "start"
});

const card = (isMobile) => ({
  minWidth: 0,
  boxSizing: "border-box",
  background: "#ffffff",
  borderRadius: 11,
  padding: isMobile ? 17 : 18,
  border: "1px solid #dce2e9",
  boxShadow: "0 1px 2px rgba(15, 23, 42, 0.03)",
  cursor: "pointer",
  transition: "box-shadow .15s ease, border-color .15s ease",
  WebkitTapHighlightColor: "transparent"
});

const cardHeader = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "flex-start",
  gap: 10,
  minWidth: 0,
  paddingBottom: 13,
  marginBottom: 13,
  borderBottom: "1px solid #e8edf2"
};

const tourTitle = (isMobile) => ({
  flex: "1 1 auto",
  minWidth: 0,
  maxWidth: "100%",
  margin: 0,
  fontSize: isMobile ? 17 : 16,
  lineHeight: 1.35,
  fontWeight: 700,
  color: "#172033",
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis"
});

const badge = {
  flexShrink: 0,
  alignSelf: "flex-start",
  fontSize: 11,
  fontWeight: 650,
  padding: "5px 10px",
  borderRadius: 999,
  lineHeight: 1,
  whiteSpace: "nowrap"
};

const activeBadge = {
  background: "#ecfdf3",
  border: "1px solid #b7ebc8",
  color: "#16834a"
};

const fullBadge = {
  background: "#fff1f0",
  border: "1px solid #f4c7c3",
  color: "#c24136"
};

const detailItem = {
  display: "flex",
  alignItems: "center",
  gap: 11,
  minWidth: 0
};

const detailIcon = {
  display: "grid",
  placeItems: "center",
  flexShrink: 0,
  width: 28,
  height: 28,
  borderRadius: 8,
  background: "#fff3ed",
  color: "#c75028",
  fontSize: 15
};

const detailLabel = {
  marginBottom: 3,
  fontSize: 10,
  lineHeight: 1.2,
  fontWeight: 700,
  letterSpacing: ".06em",
  color: "#8792a2"
};

const detailValue = {
  fontSize: 13,
  lineHeight: 1.4,
  fontWeight: 650,
  color: "#263247",
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
  flex: "1 1 0"
};

const guestSection = {
  marginTop: 14,
  paddingTop: 13,
  borderTop: "1px solid #e8edf2"
};

const guestHeader = {
  display: "flex",
  alignItems: "flex-end",
  justifyContent: "space-between",
  gap: 10
};

const guestCount = {
  display: "block",
  fontSize: 17,
  lineHeight: 1.2,
  color: "#172033"
};

const languageRow = {
  display: "flex",
  alignItems: "center",
  gap: 11,
  marginTop: 14,
  paddingTop: 12,
  borderTop: "1px solid #e8edf2"
};

const languageIcon = {
  display: "grid",
  placeItems: "center",
  flexShrink: 0,
  width: 28,
  height: 28,
  borderRadius: 8,
  background: "#fff3ed",
  color: "#c75028",
  fontSize: 13,
  fontWeight: 700
};

const infoRow = (isMobile) => ({
  minWidth: 0,
  fontSize: isMobile ? 14 : 13,
  lineHeight: 1.45,
  fontWeight: 550,
  color: "#263247",
  marginBottom: 9,
  overflowWrap: "anywhere"
});

const barBg = {
  height: 7,
  background: "#edf0f3",
  borderRadius: 999,
  overflow: "hidden",
  marginTop: 10
};

const barFill = {
  height: "100%",
  borderRadius: 999,
  transition: "width .2s ease"
};

const percentText = {
  fontSize: 12,
  marginTop: 0,
  marginBottom: 2,
  color: "#718096",
  textAlign: "right",
  whiteSpace: "nowrap"
};

const primaryBtn = (isMobile) => ({
  marginTop: 14,
  width: "100%",
  boxSizing: "border-box",
  padding: isMobile ? 13 : 10,
  borderRadius: 8,
  border: "none",
  background: "#c75028",
  color: "#ffffff",
  fontWeight: 700,
  cursor: "pointer",
  fontSize: isMobile ? 15 : 13
});

const mobileHeader = {
  padding: "15px 16px",
  margin: "-12px -12px 16px",
  background: "#f5f6f8",
  position: "sticky",
  top: 0,
  zIndex: 10,
  borderBottom: "1px solid #e1e5ea"
};

const mobileTitle = {
  margin: 0,
  fontSize: 22,
  fontWeight: 750,
  color: "#111827"
};

const searchBar = {
  display: "flex",
  justifyContent: "flex-end",
  marginBottom: 12,
  width: "100%",
  maxWidth: "100%",
  minWidth: 0,
  boxSizing: "border-box"
};

const filterButton = {
  width: 46,
  height: 42,
  borderRadius: 8,
  border: "none",
  background: "#c75028",
  color: "#ffffff",
  fontSize: 19,
  cursor: "pointer"
};

const filterPanel = {
  background: "#ffffff",
  padding: 14,
  border: "1px solid #dce2e9",
  borderRadius: 11,
  marginBottom: 15,
  display: "grid",
  gap: 10,
  width: "100%",
  maxWidth: "100%",
  minWidth: 0,
  boxSizing: "border-box"
};

const modalOverlay = {
  position: "fixed",
  inset: 0,
  background: "rgba(15, 23, 42, 0.48)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: 20,
  zIndex: 1000
};

const modal = {
  width: "100%",
  maxWidth: 420,
  boxSizing: "border-box",
  background: "#ffffff",
  border: "1px solid #e1e5ea",
  borderRadius: 13,
  padding: 24,
  boxShadow: "0 18px 45px rgba(15, 23, 42, 0.18)"
};

const modalTitle = {
  margin: "0 0 10px",
  fontSize: 20,
  fontWeight: 700,
  color: "#172033"
};

const modalText = {
  margin: "0 0 24px",
  fontSize: 14,
  lineHeight: 1.5,
  color: "#718096"
};

const modalActions = {
  display: "flex",
  gap: 10,
  justifyContent: "flex-end",
  flexWrap: "wrap"
};

const primaryModalBtn = {
  padding: "10px 17px",
  borderRadius: 8,
  border: "none",
  background: "#c75028",
  color: "#ffffff",
  fontWeight: 700,
  cursor: "pointer"
};

const secondaryModalBtn = {
  padding: "10px 17px",
  borderRadius: 8,
  border: "1px solid #d5dce5",
  background: "#ffffff",
  color: "#344054",
  fontWeight: 600,
  cursor: "pointer"
};

const tourFilterWrapper = {
  position: "relative",
  minWidth: 0,
  width: "100%"
};

const tourFilterButton = {
  width: "100%",
  minHeight: 40,
  boxSizing: "border-box",
  padding: "9px 12px",
  border: "1px solid #d5dce5",
  borderRadius: 8,
  background: "#ffffff",
  color: "#263247",
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: 10,
  cursor: "pointer",
  fontSize: 13,
  textAlign: "left"
};

const tourDropdown = {
  position: "absolute",
  top: "calc(100% + 5px)",
  left: 0,
  right: 0,
  maxHeight: 300,
  overflowY: "auto",
  background: "#ffffff",
  border: "1px solid #d5dce5",
  borderRadius: 8,
  boxShadow: "0 8px 20px rgba(15, 23, 42, 0.12)",
  zIndex: 1000,
  padding: 6
};

const tourSelectAll = {
  width: "100%",
  padding: "10px",
  border: "none",
  borderBottom: "1px solid #e8edf2",
  background: "#ffffff",
  color: "#263247",
  cursor: "pointer",
  textAlign: "left",
  fontWeight: 700
};

const tourOption = {
  display: "flex",
  alignItems: "center",
  gap: 9,
  padding: "10px",
  cursor: "pointer",
  color: "#344054",
  fontSize: 13
};
